import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguages } from '../../hooks/useLanguages';
import { useCatalog } from '../../hooks/useCatalog';
import { useAuth } from '../../context/AuthContext';
import {
  loadConversationCategories,
  loadConversationLegacyChapter,
  mergeConversationCategories,
} from '../../services/conversationV2';
import { LearningProgressService } from '../../services/LearningProgressService';
import { dataImagesUrl } from '../../services/dataApi';
import TappableText from '../../components/TappableText';
import {
  getConversationSpeechText,
  isConversationNarration,
  parseConversationLine,
} from '../../utils/conversationText';

function chaptersAreParallel(target, native) {
  if (!target || !native || Number(target.chapterId) !== Number(native.chapterId)) return false;
  const targetLineIds = Array.isArray(target.lineIds) ? target.lineIds : [];
  const nativeLineIds = Array.isArray(native.lineIds) ? native.lineIds : [];
  if (targetLineIds.length > 0 || nativeLineIds.length > 0) {
    if (targetLineIds.length !== nativeLineIds.length
      || targetLineIds.some((id, index) => id !== nativeLineIds[index])) return false;
  }
  const targetKeys = Object.keys(target.content ?? {}).sort((a, b) => Number(a) - Number(b));
  const nativeKeys = Object.keys(native.content ?? {}).sort((a, b) => Number(a) - Number(b));
  if (targetKeys.length !== nativeKeys.length || targetKeys.some((key, index) => key !== nativeKeys[index])) {
    return false;
  }
  const targetImages = Array.isArray(target.images) ? target.images : [];
  const nativeImages = Array.isArray(native.images) ? native.images : [];
  return targetImages.length === nativeImages.length
    && targetImages.every((image, index) => image === nativeImages[index]);
}

export default function ConversationLearnInline() {
  const { t } = useTranslation();
  const { languages } = useLanguages();
  const { catalog } = useCatalog();
  const { languagePrefs } = useAuth();

  const conversationLangs = languages.filter(
    (l) => catalog?.[l.code]?.features?.includes('conversation')
  );

  const [selectedCode, setSelectedCode] = useState('');

  const [index, setIndex] = useState([]);
  const [indexLoading, setIndexLoading] = useState(false);

  const [chapter, setChapter] = useState(null);
  const [nativeChapter, setNativeChapter] = useState(null);
  const [chapterLoading, setChapterLoading] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState(null);

  const [showBilingual, setShowBilingual] = useState(false);

  useEffect(() => { showBilingualRef.current = showBilingual; }, [showBilingual]);

  const [activeLineIndex, setActiveLineIndex] = useState(null);
  const [failedImages, setFailedImages] = useState({});
  const [ttsAvailable, setTtsAvailable] = useState(true);
  const [speed, setSpeed] = useState(0.75);

  const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);
  const playingRef = useRef(false);
  const currentSeqIndexRef = useRef(null);
  const showBilingualRef = useRef(showBilingual);
  const chapterRequestRef = useRef(0);

  const nativeCode = languagePrefs?.nativeLanguage || '';
  const nativeLang = languages.find((l) => l.code === nativeCode);
  const nativeTtsLang = nativeLang?.tts || '';

  useEffect(() => {
    const selectedIsAvailable = conversationLangs.some((language) => language.code === selectedCode);
    if ((!selectedCode || !selectedIsAvailable) && conversationLangs.length > 0) {
      const preferred = conversationLangs.find(
        (language) => language.code === languagePrefs?.targetLanguage,
      );
      setSelectedCode(preferred?.code || conversationLangs[0].code);
    }
  }, [conversationLangs, languagePrefs?.targetLanguage, selectedCode]);

  useEffect(() => {
    if (!(typeof window !== 'undefined' && 'speechSynthesis' in window)) {
      setTtsAvailable(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedCode) return;
    let active = true;
    chapterRequestRef.current += 1;
    setIndexLoading(true);
    setIndex([]);
    setChapter(null);
    setActiveChapterId(null);
    stopSpeech();

    const targetPromise = loadConversationCategories(selectedCode);
    Promise.all([
      targetPromise,
      nativeCode === selectedCode
        ? targetPromise
        : loadConversationCategories(nativeCode || 'en').catch(() => []),
    ])
      .then(([targetCategories, nativeCategories]) => {
        if (!active) return;
        const list = mergeConversationCategories(targetCategories, nativeCategories);
        setIndex(list);
        if (list.length > 0) {
          loadChapter(selectedCode, list[0].id);
        }
      })
      .catch(() => {
        if (active) setIndex([]);
      })
      .finally(() => {
        if (active) setIndexLoading(false);
      });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeCode, selectedCode]);

  function loadChapter(langCode, id) {
    const requestId = ++chapterRequestRef.current;
    setChapterLoading(true);
    setChapter(null);
    setNativeChapter(null);
    setActiveLineIndex(0);
    stopSpeech();

    const fetches = [loadConversationLegacyChapter(langCode, id)];
    const native = languagePrefs?.nativeLanguage;
    if (native && native !== langCode) {
      fetches.push(loadConversationLegacyChapter(native, id).catch(() => null));
    }

    Promise.all(fetches)
      .then(async ([json, nativeJson]) => {
        let resolvedNative = nativeJson;
        if (native && native !== 'en' && !chaptersAreParallel(json, resolvedNative)) {
          resolvedNative = await loadConversationLegacyChapter('en', id).catch(() => null);
        }
        if (requestId !== chapterRequestRef.current) return;
        setChapter(json);
        setActiveChapterId(id);
        const pair = languagePrefs
          ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
          : `en_${langCode}`;
        LearningProgressService.record(pair, 'conversations', 1, {
          activityId: `chapter:${id}`,
        });
        if (chaptersAreParallel(json, resolvedNative)) setNativeChapter(resolvedNative);
      })
      .catch(() => {
        if (requestId === chapterRequestRef.current) setChapter(null);
      })
      .finally(() => {
        if (requestId === chapterRequestRef.current) setChapterLoading(false);
      });
  }

  function loadNativeChapter(id) {
    const native = languagePrefs?.nativeLanguage;
    if (!native || !id) return;
    const requestId = chapterRequestRef.current;
    loadConversationLegacyChapter(native, id)
      .catch(() => null)
      .then(async (json) => {
        let resolved = json;
        if (native !== 'en' && !chaptersAreParallel(chapter, resolved)) {
          resolved = await loadConversationLegacyChapter('en', id).catch(() => null);
        }
        if (requestId !== chapterRequestRef.current) return;
        if (chaptersAreParallel(chapter, resolved)) setNativeChapter(resolved);
      })
      .catch(() => {});
  }

  function handleChapterSelect(id) {
    if (id === activeChapterId) return;
    loadChapter(selectedCode, id);
  }

  const currentLang = languages.find((l) => l.code === selectedCode);
  const ttsLang = currentLang?.tts || '';

  function speak(text, onend) {
    speakWithTts(text, ttsLang, onend);
  }

  function stopSpeech() {
    if (synthRef.current) synthRef.current.cancel();
    playingRef.current = false;
    currentSeqIndexRef.current = null;
  }

  function handleImageError(src) {
    setFailedImages((prev) => ({ ...prev, [src]: true }));
  }

  function chapterLines(ch) {
    if (!ch?.content) return [];
    return Object.entries(ch.content)
      .map(([k, v]) => ({ k: Number(k), text: v }))
      .sort((a, b) => a.k - b.k);
  }

  function speakerColor(speaker) {
    if (!speaker) return 'text-slate-700';
    const lower = speaker.toLowerCase();
    if (lower.includes('mom') || lower.includes('mama')) return 'text-pink-600';
    if (lower.includes('baby')) return 'text-blue-600';
    if (lower.includes('dad') || lower.includes('papa')) return 'text-emerald-700';
    if (lower.includes('lily') || lower.includes('emma')) return 'text-violet-600';
    return 'text-indigo-700';
  }

  function speakWithTts(text, lang, onend) {
    const speechText = getConversationSpeechText(text);
    if (!ttsAvailable || !speechText) return false;
    try {
      synthRef.current.cancel();
      const ut = new SpeechSynthesisUtterance(speechText);
      ut.lang = lang;
      ut.rate = speed;
      if (typeof onend === 'function') ut.onend = onend;
      ut.onerror = () => { playingRef.current = false; currentSeqIndexRef.current = null; };
      synthRef.current.speak(ut);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  function playSequenceFromIndex(startIdx = 0) {
    if (!chapter || !ttsAvailable) return;
    const lines = chapterLines(chapter);
    const nativeLines = nativeChapter ? chapterLines(nativeChapter) : [];
    if (!lines.length) return;
    stopSpeech();
    playingRef.current = true;
    currentSeqIndexRef.current = startIdx;

    const playNext = () => {
      const idx = currentSeqIndexRef.current;
      if (!playingRef.current || idx == null || idx >= lines.length) {
        playingRef.current = false;
        currentSeqIndexRef.current = null;
        return;
      }
      setActiveLineIndex(idx);
      const speechText = getConversationSpeechText(lines[idx].text);
      if (!speechText) {
        currentSeqIndexRef.current = idx + 1;
        setTimeout(playNext, 120);
        return;
      }
      speakWithTts(lines[idx].text, ttsLang, () => {
        if (!playingRef.current) { currentSeqIndexRef.current = null; return; }
        if (showBilingualRef.current && nativeLines.length > idx) {
          const nativeSpeechText = getConversationSpeechText(nativeLines[idx].text);
          if (nativeSpeechText) {
            speakWithTts(nativeLines[idx].text, nativeTtsLang, () => {
              if (!playingRef.current) { currentSeqIndexRef.current = null; return; }
              currentSeqIndexRef.current = idx + 1;
              setTimeout(playNext, 120);
            });
            return;
          }
        }
        currentSeqIndexRef.current = idx + 1;
        setTimeout(playNext, 120);
      });
    };
    setTimeout(playNext, 100);
  }

  const lines = chapterLines(chapter);
  const nativeLines = nativeChapter ? chapterLines(nativeChapter) : [];
  const activeIndexChapter = index.find((item) => String(item.id) === String(activeChapterId));
  const activeDisplayNumber = activeIndexChapter?.chapterId ?? activeChapterId;

  function handleBilingualToggle() {
    const next = !showBilingual;
    setShowBilingual(next);
    if (next && !nativeChapter && activeChapterId) {
      loadNativeChapter(activeChapterId);
    }
  }

  const speedLabel = speed === 0.5 ? t('conversation.slow')
    : speed === 0.75 ? t('conversation.medium')
    : speed === 1.0  ? t('conversation.normal')
    : speed === 1.25 ? t('conversation.fast')
    : t('conversation.veryFast');

  const LEVELS = [
    { key: 'simple',    label: t('conversation.simple'),   dot: 'bg-emerald-500', text: 'text-emerald-700' },
    { key: 'medium',    label: t('conversation.medium'),   dot: 'bg-amber-500',   text: 'text-amber-700'   },
    { key: 'hard',      label: t('conversation.hard'),     dot: 'bg-orange-500',  text: 'text-orange-700'  },
    { key: 'very_hard', label: t('conversation.veryHard'), dot: 'bg-rose-500',    text: 'text-rose-700'    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-violet-50/30">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Sidebar ── */}
          <aside className="w-full lg:w-72 shrink-0">
            <div className="sticky top-6 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">

              {/* Language selector */}
              <div className="border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-4">
                <p className="mb-2 text-xs font-black uppercase tracking-widest text-indigo-500">{t('conversation.language')}</p>
                <select
                  value={selectedCode}
                  onChange={(e) => setSelectedCode(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  {conversationLangs.map((l) => (
                    <option key={l.code} value={l.code}>
                      {`${l.label}${l.nativeLabel && l.nativeLabel !== l.label ? ` — ${l.nativeLabel}` : ''}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chapter list */}
              <div className="px-3 py-3">
                {indexLoading ? (
                  <div className="space-y-2 p-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />
                    ))}
                  </div>
                ) : (
                  <div className="max-h-[calc(100vh-240px)] overflow-y-auto pr-1 space-y-3">
                    {LEVELS.map(({ key, label, dot, text }) => {
                      const group = index
                        .filter((ch) => ch.level === key)
                        .sort((a, b) => Number(a.levelOrder) - Number(b.levelOrder));
                      if (!group.length) return null;
                      return (
                        <div key={key}>
                          <div className="flex items-center gap-1.5 px-1 mb-1">
                            <span className={`h-2 w-2 rounded-full ${dot}`} />
                            <p className={`text-[10px] font-black uppercase tracking-widest ${text}`}>{label}</p>
                          </div>
                          <div className="space-y-0.5">
                            {group.map((ch) => (
                              <button
                                key={ch.id}
                                onClick={() => handleChapterSelect(ch.id)}
                                className={`w-full rounded-xl px-3 py-2.5 text-left transition-all ${
                                  activeChapterId === ch.id
                                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md'
                                    : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs font-black flex-shrink-0 ${activeChapterId === ch.id ? 'text-white/60' : 'text-slate-400'}`}>
                                    {String(ch.chapterId ?? ch.id).padStart(2, '0')}
                                  </span>
                                  <span dir={currentLang?.dir || 'ltr'} className="text-sm font-bold leading-tight truncate">{ch.title}</span>
                                </div>
                                {ch.nativeTitle && ch.nativeTitle !== ch.title && (
                                  <p
                                    dir={nativeLang?.dir || 'ltr'}
                                    className={`ml-6 mt-1 truncate text-xs font-semibold leading-tight ${
                                      activeChapterId === ch.id ? 'text-white/80' : 'text-violet-700'
                                    }`}
                                  >
                                    {ch.nativeTitle}
                                  </p>
                                )}
                                <p dir={currentLang?.dir || 'ltr'} className={`mt-0.5 text-xs leading-tight truncate ${activeChapterId === ch.id ? 'text-white/60' : 'text-slate-400'} ml-6`}>
                                  {ch.scene}
                                </p>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* ── Main content ── */}
          <main className="flex-1 min-w-0">

            {/* Page title */}
            <div className="mb-6">
              <h1 className="text-3xl font-black text-slate-900">{t('conversation.title')}</h1>
              <p className="mt-1 text-sm text-slate-500">{t('conversation.subtitle')}</p>
            </div>

            {chapterLoading && (
              <div className="space-y-4">
                <div className="h-8 w-2/3 animate-pulse rounded-xl bg-slate-200" />
                <div className="h-4 w-1/3 animate-pulse rounded-xl bg-slate-100" />
                <div className="mt-6 space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  ))}
                </div>
              </div>
            )}

            {!chapterLoading && chapter && (
              <article className="space-y-5">

                {/* Chapter header */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 text-xs font-black text-indigo-700">
                      {String(activeDisplayNumber).padStart(2, '0')}
                    </div>
                    <div className="min-w-0">
                      <h2 dir={currentLang?.dir || 'ltr'} className="text-xl font-black text-slate-900">{chapter.title}</h2>
                      <p dir={currentLang?.dir || 'ltr'} className="mt-0.5 text-sm font-semibold text-indigo-600">{chapter.scene}</p>
                      {chapter.context && (
                        <p dir={currentLang?.dir || 'ltr'} className="mt-2 text-sm leading-relaxed text-slate-500">{chapter.context}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Images */}
                {Array.isArray(chapter.images) && chapter.images.length > 0 && (
                  <div className="flex gap-3 overflow-x-auto pb-1">
                    {chapter.images.map((img) => {
                      const src = dataImagesUrl(`conversation/${img}`);
                      if (failedImages[src]) return null;
                      return (
                        <img
                          key={src}
                          src={src}
                          alt={chapter.title || ''}
                          onError={() => handleImageError(src)}
                          className="h-44 w-64 shrink-0 rounded-2xl object-cover shadow-md"
                        />
                      );
                    })}
                  </div>
                )}

                {/* Dialogue */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t('conversation.dialogue')}</p>
                    {nativeChapter && <button
                      onClick={handleBilingualToggle}
                      className={`flex items-center gap-1.5 rounded-lg border px-3 py-1 text-xs font-bold transition ${
                        showBilingual
                          ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                      {showBilingual ? t('conversation.bilingualOn') : t('conversation.bilingual')}
                    </button>}
                  </div>
                  {showBilingual && !nativeCode && (
                    <div className="mx-3 mt-2 mb-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 font-semibold">
                      {t('conversation.setLanguages')}
                    </div>
                  )}
                  <div className="divide-y divide-slate-50 px-2 py-2">
                    {lines.map((ln, idx) => {
                      const { speaker, line } = parseConversationLine(ln.text);
                      const isNarration = isConversationNarration(ln.text);
                      const speechText = getConversationSpeechText(ln.text);
                      const nativeLn = showBilingual && nativeLines.length > idx ? nativeLines[idx] : null;
                      const nativeParsed = nativeLn ? parseConversationLine(nativeLn.text) : null;
                      return (
                        <div
                          key={ln.k}
                          className={`flex items-start gap-3 rounded-xl px-3 py-3 transition-all duration-200 ${
                            activeLineIndex === idx
                              ? 'bg-indigo-50 ring-1 ring-indigo-200'
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            {speaker && !isNarration && (
                              <p className={`text-xs font-black uppercase tracking-wide mb-1 ${speakerColor(speaker)}`}>
                                {speaker}
                              </p>
                            )}
                            <TappableText
                              text={line}
                              nativeCode={nativeCode}
                              targetCode={selectedCode}
                              className={`leading-relaxed ${isNarration ? 'text-sm italic text-slate-400' : 'text-slate-800'}`}
                            />
                            {nativeParsed && (
                              <p className="mt-1 text-xs italic text-slate-400 leading-relaxed">
                                {nativeParsed.line}
                              </p>
                            )}
                          </div>

                          {ttsAvailable && speechText && (
                            <div className="flex shrink-0 items-center gap-1.5">
                              <button
                                onClick={() => { stopSpeech(); speak(ln.text, () => {}); setActiveLineIndex(idx); }}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600"
                                title={t('exercise.playAudio')}
                              >
                                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => playSequenceFromIndex(idx)}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600"
                                title={t('conversation.playAll')}
                              >
                                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* TTS controls */}
                {ttsAvailable && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-black uppercase tracking-widest text-slate-400 shrink-0">{t('conversation.speed')}</span>
                      <input
                        type="range"
                        min="0.5" max="1.5" step="0.25"
                        value={speed}
                        onChange={(e) => setSpeed(parseFloat(e.target.value))}
                        className="flex-1 accent-indigo-600"
                      />
                      <span className="w-20 text-right text-xs font-bold text-indigo-600">
                        {speedLabel}
                      </span>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => playSequenceFromIndex(0)}
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5"
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        {t('conversation.playAll')}
                      </button>
                      <button
                        onClick={stopSpeech}
                        className="rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-bold text-red-600 transition hover:bg-red-100"
                      >
                        {t('conversation.stop')}
                      </button>
                    </div>
                  </div>
                )}

              </article>
            )}

            {!chapterLoading && !chapter && !indexLoading && (
              <div className="flex h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-center">
                <p className="text-4xl mb-3">💬</p>
                <p className="font-bold text-slate-600">{t('conversation.selectChapter')}</p>
              </div>
            )}
          </main>

        </div>
      </div>
    </div>
  );
}
